/**
 * ChartForm Component
 * 
 * Form for creating and editing charts
 * 
 * Features:
 * - Create new charts
 * - Edit existing charts
 * - Input validation
 * - Real-time character counts
 * - Preview of chart URL
 * - Tag management with suggestions from existing tags
 */

import React, { useState, useEffect } from 'react';

// Use environment variable for API URL, fallback to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const ChartForm = ({ chart, onSubmit, onCancel, isSubmitting }) => {
  // Form data state
  const [formData, setFormData] = useState({
    title: '',
    tooltip: '',
    url: '',
    tags: []  // Array of tag strings
  });
  
  // UI state
  const [errors, setErrors] = useState({});
  const [currentTagInput, setCurrentTagInput] = useState('');  // Current text in tag input field
  const [existingTags, setExistingTags] = useState([]);  // All available tags from backend
  const [showSuggestions, setShowSuggestions] = useState(false);  // Show/hide suggestions dropdown

  // Fetch existing tags from backend on component mount
  useEffect(() => {
    const fetchExistingTags = async () => {
      try {
        const response = await fetch(`${API_URL}/getAllTags`);
        if (response.ok) {
          const data = await response.json();
          // Extract tag strings from response objects
          const tagStrings = data.map(item => item.tag);
          setExistingTags(tagStrings);
        }
      } catch (error) {
        console.error('Error fetching existing tags:', error);
      }
    };
    
    fetchExistingTags();
  }, []);

  // Populate form if editing existing chart
  useEffect(() => {
    if (chart) {
      setFormData({
        title: chart.title || '',
        tooltip: chart.tooltip || '',
        url: chart.url || '',
        tags: chart.tags || []  // Load existing tags or empty array
      });
    }
  }, [chart]);

  /**
   * Handle input changes for title, tooltip, and url fields
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Handle tag input field changes
   * Shows/hides suggestions based on input
   */
  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setCurrentTagInput(value);
    setShowSuggestions(value.trim().length > 0);
    
    // Clear tag errors when user starts typing
    if (errors.tags) {
      setErrors(prev => ({
        ...prev,
        tags: ''
      }));
    }
  };

  /**
   * Add a new tag to the form data
   * Validates tag before adding (not empty, not duplicate, max 10 tags, max 30 chars)
   */
  const handleAddTag = () => {
    const tagToAdd = currentTagInput.trim();
    
    // Validation checks
    if (!tagToAdd) {
      return;  // Empty tag, do nothing
    }
    
    if (tagToAdd.length > 30) {
      setErrors(prev => ({
        ...prev,
        tags: 'Tag must be 30 characters or less'
      }));
      return;
    }
    
    if (formData.tags.includes(tagToAdd)) {
      setErrors(prev => ({
        ...prev,
        tags: 'This tag has already been added'
      }));
      return;
    }
    
    if (formData.tags.length >= 10) {
      setErrors(prev => ({
        ...prev,
        tags: 'Maximum 10 tags allowed'
      }));
      return;
    }
    
    // Add the tag
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tagToAdd]
    }));
    
    // Clear input and hide suggestions
    setCurrentTagInput('');
    setShowSuggestions(false);
  };

  /**
   * Remove a tag from the form data by index
   */
  const handleRemoveTag = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, index) => index !== indexToRemove)
    }));
    
    // Clear tag errors when removing tags
    if (errors.tags) {
      setErrors(prev => ({
        ...prev,
        tags: ''
      }));
    }
  };

  /**
   * Add a tag from the suggestions dropdown
   */
  const handleSuggestionClick = (tagToAdd) => {
    // Check if tag already exists
    if (formData.tags.includes(tagToAdd)) {
      setErrors(prev => ({
        ...prev,
        tags: 'This tag has already been added'
      }));
      return;
    }
    
    if (formData.tags.length >= 10) {
      setErrors(prev => ({
        ...prev,
        tags: 'Maximum 10 tags allowed'
      }));
      return;
    }
    
    // Add the tag
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tagToAdd]
    }));
    
    // Clear input and hide suggestions
    setCurrentTagInput('');
    setShowSuggestions(false);
  };

  /**
   * Filter existing tags based on current input
   * Returns tags that match the input and aren't already added
   */
  const getFilteredSuggestions = () => {
    if (!currentTagInput.trim()) {
      return [];
    }
    
    const input = currentTagInput.toLowerCase();
    return existingTags
      .filter(tag => 
        tag.toLowerCase().includes(input) &&
        !formData.tags.includes(tag)
      )
      .slice(0, 5);  // Limit to 5 suggestions
  };

  /**
   * Validate form data including tags
   */
  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 50) {
      newErrors.title = 'Title must be 50 characters or less';
    }

    // Tooltip validation
    if (!formData.tooltip.trim()) {
      newErrors.tooltip = 'Tooltip is required';
    } else if (formData.tooltip.length > 5000) {
      newErrors.tooltip = 'Tooltip is too long (max 5000 characters)';
    }

    // URL validation
    if (!formData.url.trim()) {
      newErrors.url = 'URL is required';
    } else if (!formData.url.startsWith('/')) {
      newErrors.url = 'URL must start with / (e.g., /ytd/mag7/)';
    }

    // Tags validation (optional field, but if provided must meet requirements)
    if (formData.tags.length > 10) {
      newErrors.tags = 'Maximum 10 tags allowed';
    }
    
    // Validate each tag
    formData.tags.forEach((tag, index) => {
      if (tag.length > 30) {
        newErrors.tags = `Tag "${tag.substring(0, 20)}..." exceeds 30 character limit`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  /**
   * Handle cancel
   * Resets all form fields including tags
   */
  const handleCancel = () => {
    setFormData({ title: '', tooltip: '', url: '', tags: [] });
    setCurrentTagInput('');
    setShowSuggestions(false);
    setErrors({});
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className="chart-form">
      <h3>{chart ? 'Edit Chart' : 'Create New Chart'}</h3>

      {/* Title Input */}
      <div className="form-group">
        <label htmlFor="title">
          Chart Title <span className="required">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., Year-to-Date SPX Performance"
          maxLength={50}
          disabled={isSubmitting}
          className={errors.title ? 'error' : ''}
        />
        <div className="field-info">
          <span className={errors.title ? 'error-text' : ''}>
            {errors.title || `${formData.title.length}/50 characters`}
          </span>
        </div>
      </div>

      {/* Tooltip Input */}
      <div className="form-group">
        <label htmlFor="tooltip">
          Tooltip / Description <span className="required">*</span>
        </label>
        <textarea
          id="tooltip"
          name="tooltip"
          value={formData.tooltip}
          onChange={handleChange}
          placeholder="Describe what this chart shows and what users can learn from it..."
          rows={4}
          maxLength={5000}
          disabled={isSubmitting}
          className={errors.tooltip ? 'error' : ''}
        />
        <div className="field-info">
          <span className={errors.tooltip ? 'error-text' : ''}>
            {errors.tooltip || `${formData.tooltip.length}/5000 characters`}
          </span>
        </div>
      </div>

      {/* URL Input */}
      <div className="form-group">
        <label htmlFor="url">
          Chart URL Path <span className="required">*</span>
        </label>
        <input
          type="text"
          id="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          placeholder="/ytd/mag7/"
          disabled={isSubmitting}
          className={errors.url ? 'error' : ''}
        />
        <div className="field-info">
          <span className={errors.url ? 'error-text' : ''}>
            {errors.url || 'Must start with / (e.g., /ytd/mag7/)'}
          </span>
        </div>
        {formData.url && formData.url.startsWith('/') && (
          <div className="url-preview">
            <strong>Preview:</strong> <code>{formData.url}</code>
          </div>
        )}
      </div>

      {/* Tags Input */}
      <div className="form-group">
        <label htmlFor="tags">
          Tags (Optional)
        </label>
        <div className="tag-input-container">
          <input
            type="text"
            id="tags"
            value={currentTagInput}
            onChange={handleTagInputChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            placeholder="Type a tag and press Enter or click Add..."
            maxLength={30}
            disabled={isSubmitting || formData.tags.length >= 10}
            className={errors.tags ? 'error' : ''}
          />
          <button
            type="button"
            onClick={handleAddTag}
            disabled={!currentTagInput.trim() || isSubmitting || formData.tags.length >= 10}
            className="add-tag-btn"
          >
            Add Tag
          </button>
        </div>

        {/* Tag Suggestions Dropdown */}
        {showSuggestions && getFilteredSuggestions().length > 0 && (
          <div className="tag-suggestions">
            <div className="suggestions-header">Existing tags:</div>
            {getFilteredSuggestions().map((suggestion, index) => (
              <div
                key={index}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}

        {/* Display Current Tags as Chips */}
        {formData.tags.length > 0 && (
          <div className="tag-chips-container">
            {formData.tags.map((tag, index) => (
              <div key={index} className="tag-chip">
                <span className="tag-text">{tag}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(index)}
                  disabled={isSubmitting}
                  className="remove-tag-btn"
                  title="Remove tag"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tag Counter and Error Display */}
        <div className="field-info">
          <span className={errors.tags ? 'error-text' : ''}>
            {errors.tags || `${formData.tags.length}/10 tags`}
          </span>
          {currentTagInput && (
            <span className="tag-char-count">
              {currentTagInput.length}/30 characters
            </span>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button
          type="button"
          onClick={handleCancel}
          className="cancel-btn"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : chart ? 'Update Chart' : 'Create Chart'}
        </button>
      </div>
    </form>
  );
};

export default ChartForm;

